import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

/**
 * Public payment page for the WhatsApp bot flow.
 *
 * WhatsApp bot sends the patient `https://neuroopinion.in/pay/{consultationId}`.
 * Which gateway loads is decided by the case's `currency`:
 *   - INR → Cashfree JS SDK (auto-launches checkout, redirect_target=_self)
 *   - USD → PayPal JS SDK (renders PayPal buttons; Guest-Checkout enabled)
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const loadScript = (src, id) => new Promise((resolve) => {
  if (id && document.getElementById(id)) return resolve(true);
  const s = document.createElement("script");
  s.src = src;
  if (id) s.id = id;
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function PublicPay() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", msg: "Fetching your payment details…" });
  const [payInfo, setPayInfo] = useState(null);
  const paypalContainer = useRef(null);
  const paypalRendered = useRef(false);
  const mainRan = useRef(false);

  // ── PayPal button render effect ────────────────────────────────────
  // Runs after `payInfo` is set to a PayPal case. The container div is
  // always mounted (hidden until phase=="paypal") so the ref is stable
  // across React StrictMode double-invocation.
  useEffect(() => {
    if (state.phase !== "paypal" || !payInfo || payInfo.gateway !== "paypal") return;
    if (paypalRendered.current) return;
    if (!paypalContainer.current) return;
    paypalRendered.current = true;  // set BEFORE async work to avoid double render in StrictMode
    let cancelled = false;
    (async () => {
      const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${payInfo.paypal_client_id}&currency=USD&intent=capture&enable-funding=card`;
      const ok = await loadScript(sdkUrl, "paypal-sdk");
      if (cancelled) return;
      if (!ok || !window.paypal) {
        paypalRendered.current = false;
        setState({ phase: "error", msg: "Could not load PayPal. Please refresh and try again." });
        return;
      }
      try {
        await window.paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 },
          createOrder: async () => {
            const co = await axios.post(`${API_BASE}/api/payments/paypal/create-order/${cid}`);
            if (co.data?.order_id) return co.data.order_id;
            throw new Error("Failed to create PayPal order");
          },
          onApprove: async () => {
            setState({ phase: "loading", msg: "Confirming your payment…" });
            const cap = await axios.post(`${API_BASE}/api/payments/paypal/capture/${cid}`);
            if (cap.data?.ok) {
              setState({ phase: "paid", msg: "Payment confirmed! Please return to WhatsApp for updates." });
              setTimeout(() => { window.location.href = `https://wa.me/918317674160?text=Paid`; }, 1800);
            } else {
              setState({ phase: "error", msg: "Payment could not be confirmed. Please contact support." });
            }
          },
          onError: () => {
            setState({ phase: "error", msg: "Payment failed. Please try again or use a different card." });
          },
          onCancel: () => {
            setState({ phase: "paypal", msg: "Payment cancelled. You can try again below." });
          },
        }).render(paypalContainer.current);
      } catch (e) {
        paypalRendered.current = false;
        setState({ phase: "error", msg: "Could not render PayPal button. Please refresh." });
      }
    })();
    return () => { cancelled = true; };
  }, [state.phase, payInfo, cid]);

  useEffect(() => {
    if (mainRan.current) return;  // guard against StrictMode double-invoke
    mainRan.current = true;
    (async () => {
      // ── PayPal return-URL handshake ────────────────────────────────
      // PayPal doesn't retry a capture on our side automatically, so if we
      // land here with ?paid=1&gw=paypal we must call /capture to finalise.
      const params = new URLSearchParams(window.location.search);
      const paidFlag = params.get("paid") === "1";
      const gwParam  = params.get("gw");
      if (paidFlag && gwParam === "paypal") {
        try {
          const r = await axios.post(`${API_BASE}/api/payments/paypal/capture/${cid}`);
          if (r.data?.ok) {
            setState({ phase: "paid", msg: "Payment confirmed! Please return to WhatsApp for updates." });
            setTimeout(() => { window.location.href = `https://wa.me/918317674160?text=Paid`; }, 1800);
            return;
          }
        } catch (e) { /* fall through */ }
      }

      // ── Cashfree idempotent confirm (existing behaviour) ────────────
      // Safe to call even for PayPal cases — it 400s early since no
      // cashfree_order_id, which we catch below.
      try {
        const r = await axios.post(`${API_BASE}/api/payments/public/confirm/${cid}`);
        if (r.data?.ok && (r.data?.confirmed || r.data?.already_paid)) {
          setState({ phase: "paid", msg: "Payment confirmed! Please return to WhatsApp for updates." });
          setTimeout(() => { window.location.href = `https://wa.me/918317674160?text=Paid`; }, 1800);
          return;
        }
      } catch (e) { /* not fatal */ }

      if (paidFlag && gwParam !== "paypal") {
        setState({ phase: "error", msg: "Payment received by Cashfree but confirmation is still processing. Refresh in 30 seconds — the case will appear as paid automatically." });
        return;
      }

      // ── Fetch session (gateway-aware) ───────────────────────────────
      try {
        const r = await axios.get(`${API_BASE}/api/payments/public/session/${cid}`);
        if (r.data.already_paid) {
          setState({ phase: "paid", msg: "This case is already paid. Please check WhatsApp for updates." });
          setPayInfo(r.data);
          return;
        }
        setPayInfo(r.data);

        // ── PayPal branch ─────────────────────────────────────────────
        // The actual SDK load + Buttons().render() happens in the second
        // useEffect above, once React has mounted the container ref. We
        // just flip the phase here so the container div appears in DOM.
        if (r.data.gateway === "paypal") {
          setState({ phase: "paypal", msg: "Choose how you'd like to pay" });
          return;
        }

        // ── Cashfree branch (existing INR flow) ───────────────────────
        const ok = await loadScript("https://sdk.cashfree.com/js/v3/cashfree.js", "cashfree-sdk");
        if (!ok) {
          setState({ phase: "error", msg: "Could not load payment gateway. Please refresh and try again." });
          return;
        }
        setState({ phase: "checkout", msg: "Opening secure checkout…" });
        const cashfree = window.Cashfree({ mode: r.data.env === "prod" ? "production" : "sandbox" });
        await cashfree.checkout({ paymentSessionId: r.data.payment_session_id, redirectTarget: "_self" });
      } catch (e) {
        const detail = e.response?.data?.detail || e.message || "Payment link is invalid or expired.";
        const status = e.response?.status;
        // Case-not-found on the host serving THIS link means the case was
        // minted on a different backend/DB (typically Interakt-routed the
        // booking to preview, but the message came out with a prod URL).
        // Give the patient an actionable next step + a WA fallback so we
        // recover the sale instead of losing them on a 404 card.
        const isNotFound = status === 404 || /not found/i.test(detail);
        setState({
          phase: "error",
          notFound: isNotFound,
          msg: isNotFound
            ? "This payment link isn't recognised on this device — it may be from a test session or the case has moved. Tap WhatsApp below and we'll send you a fresh link within a minute."
            : detail,
        });
        try {
          window.gtag?.("event", "public_pay_error", { cid, status: status || 0, notFound: isNotFound });
        } catch (_e) { /* gtag optional */ }
      }
    })();
  }, [cid, navigate]);

  const isLoading = state.phase === "loading" || state.phase === "checkout";
  const isPaid = state.phase === "paid";
  const isError = state.phase === "error";
  const isPayPal = state.phase === "paypal";

  const gateway = payInfo?.gateway;
  const secureLine = gateway === "paypal"
    ? "100% Secure · Powered by PayPal"
    : "100% Secure · Powered by Cashfree";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 text-center" data-testid="public-pay-panel">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            {isLoading && <Loader2 className="w-8 h-8 text-emerald-600 animate-spin"/>}
            {isPaid && <CheckCircle2 className="w-9 h-9 text-emerald-600"/>}
            {isError && <XCircle className="w-9 h-9 text-red-500"/>}
            {isPayPal && <ShieldCheck className="w-9 h-9 text-emerald-600"/>}
          </div>
        </div>

        <h1 className="font-heading text-xl font-semibold text-slate-900 mb-1">
          {isPaid ? "Payment successful!" : isError ? "Something went wrong" : "NeuroOpinion Checkout"}
        </h1>
        <p className="text-sm text-slate-600 mb-5" data-testid="public-pay-status">{state.msg}</p>

        {payInfo && (
          <div className="text-left rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5 mb-4">
            <div className="flex justify-between text-[13px]">
              <span className="text-slate-500">Case</span>
              <span className="font-mono font-semibold text-slate-900">{payInfo.case_ref}</span>
            </div>
            {payInfo.patient_name && (
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-500">Patient</span>
                <span className="text-slate-900">{payInfo.patient_name}</span>
              </div>
            )}
            <div className="flex justify-between text-[13px]">
              <span className="text-slate-500">Amount</span>
              <span className="font-bold text-emerald-700">
                {payInfo.currency === "USD" ? `$${payInfo.payment_amount || payInfo.fee_rupees} USD` : `₹${payInfo.payment_amount || payInfo.fee_rupees}`}
              </span>
            </div>
            {payInfo.pricing_tier && (
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Tier</span>
                <span>{payInfo.pricing_tier} · {payInfo.service_type || "REGULAR"}</span>
              </div>
            )}
          </div>
        )}

        {isPayPal && (
          <div className="text-[11px] text-slate-500 mb-2">Secure checkout — pay with card or PayPal balance</div>
        )}
        <div ref={paypalContainer} data-testid="paypal-button-container" className={isPayPal ? "min-h-[100px] mb-2" : "hidden"} />

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-3">
          <ShieldCheck className="w-3.5 h-3.5"/> {secureLine}
        </div>

        {isError && (
          <div className="mt-4 space-y-2">
            {state.notFound && (
              <a
                href={`https://wa.me/918317674160?text=${encodeURIComponent(`Hi NeuroOpinion, my payment link says it's not recognised. Case ref: ${cid?.slice(0,8).toUpperCase() || "?"}. Please send me a fresh link.`)}`}
                target="_blank" rel="noreferrer"
                data-testid="public-pay-whatsapp-recover"
                className="block w-full py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-[13.5px] font-semibold"
              >
                💬 Get a fresh link on WhatsApp
              </a>
            )}
            <button
              onClick={() => window.location.reload()}
              data-testid="public-pay-retry"
              className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13.5px] font-semibold"
            >Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}
